import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faCalculatorSimple } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, TableBody, TableHead, TableRow, Typography } from '@mui/material';

import { calculationQueries } from '@repo/api/risk/calculations';
import { isProjectedAnnualBiValue } from '@repo/api/risk/calculations.types';
import { riskQueries } from '@repo/api/risk/risks';
import useCurrency from '@repo/features/hooks/useCurrency';

import { CalculationTable } from './-components/CalculationTable';
import { Cell } from './-components/Cell';
import { CurrencyCell } from './-components/CurrencyCell';

export const Route = createFileRoute(
	'/_risks/risk/$riskId/_risk/calculations/summary',
)({
	component: function SummaryCalculationsPage() {
		const { riskId } = Route.useParams();

		const [{ data: risk }, { data }] = useSuspenseQueries({
			queries: [riskQueries.id(riskId), calculationQueries.summary(riskId)],
		});

		const currency = useCurrency(risk.riskInsured.countryId);

		if (!isProjectedAnnualBiValue(data)) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						p: 4,
						gap: 1,
						border: '1px solid',
						borderColor: 'accent.main',
						width: '600px',
						maxWidth: '100%',
						mx: 'auto',
					}}
				>
					<FontAwesomeIcon icon={faCalculatorSimple} size="2xl" />
					<Typography variant="subtitle1">No Calculations Data</Typography>
				</Box>
			);
		}

		const {
			annualSales,
			salesTrend,
			projectedSales,
			savedCostsExPayroll,
			savedCostsIncPayroll,
			projectedAnnualBiValueExPayroll,
			projectedAnnualBiValueIncPayroll,
			hasPayrollExpenseSavedCost,
		} = data;

		const twoAmounts = hasPayrollExpenseSavedCost;

		return (
			<Box
				sx={{
					width: '420px',
					maxWidth: '100%',
				}}
			>
				<CalculationTable>
					<TableHead>
						<TableRow>
							<Cell header>Description</Cell>
							{twoAmounts ?
								<>
									<Cell header>Excluding Payroll</Cell>
									<Cell header>Including Payroll</Cell>
								</>
							:	<Cell header>Amount</Cell>}
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<Cell />
							<Cell header small>
								({currency.display ?? currency.currency})
							</Cell>
							{twoAmounts && (
								<Cell header small>
									({currency.display ?? currency.currency})
								</Cell>
							)}
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Annual Sales</Cell>
							<CurrencyCell {...currency}>{annualSales}</CurrencyCell>
							{twoAmounts && (
								<CurrencyCell {...currency}>{annualSales}</CurrencyCell>
							)}
						</TableRow>
						<TableRow className="striped">
							<Cell>Sales Trend</Cell>
							<Cell percent underline>
								{salesTrend}
							</Cell>
							{twoAmounts && (
								<Cell percent underline>
									{salesTrend}
								</Cell>
							)}
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Projected Sales</Cell>
							<CurrencyCell {...currency}>{projectedSales}</CurrencyCell>
							{twoAmounts && (
								<CurrencyCell {...currency}>{projectedSales}</CurrencyCell>
							)}
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Less: Saved Costs</Cell>
							<CurrencyCell {...currency} hideSymbol underline>
								{savedCostsExPayroll}
							</CurrencyCell>
							{twoAmounts && (
								<CurrencyCell {...currency} hideSymbol underline>
									{savedCostsIncPayroll}
								</CurrencyCell>
							)}
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Projected Annual BI Value</Cell>
							<CurrencyCell {...currency} underline="double">
								{projectedAnnualBiValueExPayroll}
							</CurrencyCell>
							{twoAmounts && (
								<CurrencyCell {...currency} underline="double">
									{projectedAnnualBiValueIncPayroll}
								</CurrencyCell>
							)}
						</TableRow>
					</TableBody>
				</CalculationTable>
			</Box>
		);
	},
});
